import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Calendar,
  Plus,
  ChevronLeft,
  ChevronRight,
  Users,
  LogOut,
  RefreshCw,
  ClipboardList,
  ShoppingCart,
  Trophy,
  X,
  MapPin,
  Printer,
  Camera,
} from "lucide-react";
import supabase from "../supabaseClient";
import {
  monthNames,
  createStripedGradient,
  isRecurringOnDate,
} from "../utils/calendarUtils";
import MonthView from "./views/MonthView";
import WeekView from "./views/WeekView";
import DayView from "./views/DayView";
import WeekendView from "./views/WeekendView";
import AgendaView from "./views/AgendaView";
import EventModal from "./EventModal";
import SettingsModal from "./SettingsModal";
import Announcement from "./Announcement";
import ChoreListView from "./views/ChoreListView";
import ChoreModal from "./ChoreModal";
import ShoppingListView from "./views/ShoppingListView";
import SeasonsView from "./SeasonsView";
import WeekAtAGlance from "./WeekAtAGlance";

const CACHE_KEY = "fc_cache";
const SEASONS_CACHE_KEY = "fc_seasons_cache";
const CHORES_CACHE_KEY = "fc_chores_cache";
const CHORE_COMPLETIONS_CACHE_KEY = "fc_chore_completions_cache";
const SHOPPING_CACHE_KEY = "fc_shopping_cache";
const CONTACTS_CACHE_KEY = "fc_contacts_cache";
const VACATIONS_CACHE_KEY = "fc_vacations_cache";
const VENUES_CACHE_KEY = "fc_venues_cache";

const fromCache = (key) => {
  try { const c = localStorage.getItem(key); return c ? JSON.parse(c) : []; } catch { return []; }
};

// ── Lazy load: load ±6 months around center + all recurring events ──────────
const buildLoadWindow = (date) => {
  const from = new Date(date);
  from.setMonth(from.getMonth() - 6);
  const to = new Date(date);
  to.setMonth(to.getMonth() + 6);
  return {
    fromStr: `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, "0")}-01`,
    toStr: `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, "0")}-${new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate()}`,
  };
};

// ── Weather helpers (Open-Meteo 7-day forecast + Nominatim geocoding) ────────
const WMO = {
  0: ["☀️", "Clear"], 1: ["🌤️", "Mostly clear"], 2: ["⛅", "Partly cloudy"],
  3: ["☁️", "Overcast"], 45: ["🌫️", "Foggy"], 48: ["🌫️", "Icy fog"],
  51: ["🌦️", "Light drizzle"], 53: ["🌦️", "Drizzle"], 55: ["🌧️", "Heavy drizzle"],
  61: ["🌧️", "Light rain"], 63: ["🌧️", "Rain"], 65: ["🌧️", "Heavy rain"],
  71: ["❄️", "Light snow"], 73: ["❄️", "Snow"], 75: ["❄️", "Heavy snow"],
  77: ["❄️", "Snow grains"], 80: ["🌦️", "Light showers"], 81: ["🌧️", "Showers"],
  82: ["⛈️", "Heavy showers"], 85: ["❄️", "Snow showers"], 86: ["❄️", "Heavy snow showers"],
  95: ["⛈️", "Thunderstorm"], 96: ["⛈️", "Thunderstorm w/ hail"], 99: ["⛈️", "Heavy thunderstorm"],
};

const WeatherBadge = ({ date, location }) => {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    if (!date || !location) return;
    const [y, m, d] = date.split("-").map(Number);
    const gameDay = new Date(y, m - 1, d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((gameDay - today) / 86400000);
    if (diffDays < 0 || diffDays > 6) return; // 7-day window

    (async () => {
      try {
        // Geocode address → lat/lon via Nominatim (OpenStreetMap)
        const geoResp = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(location)}&format=json&limit=1`,
          { headers: { "User-Agent": "FamilyCalendarApp/1.0" } }
        );
        const geoData = await geoResp.json();
        if (!geoData?.[0]) return;
        const { lat, lon } = geoData[0];

        // 7-day forecast from Open-Meteo
        const wResp = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
          `&daily=weathercode,temperature_2m_max,temperature_2m_min` +
          `&temperature_unit=fahrenheit&timezone=auto&forecast_days=7`
        );
        const wData = await wResp.json();
        const targetDate = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        const idx = wData.daily?.time?.indexOf(targetDate);
        if (idx == null || idx === -1) return;

        setWeather({
          code: wData.daily.weathercode[idx],
          high: Math.round(wData.daily.temperature_2m_max[idx]),
          low: Math.round(wData.daily.temperature_2m_min[idx]),
        });
      } catch { /* silently ignore */ }
    })();
  }, [date, location]);

  if (!weather) return null;
  const [emoji, desc] = WMO[weather.code] || ["🌤️", "Unknown"];
  const forecastUrl = `https://www.google.com/search?q=weather+${encodeURIComponent(location)}`;
  return (
    <a href={forecastUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600 underline touch-manipulation">
      {emoji} {desc} · {weather.high}°/{weather.low}°F
    </a>
  );
};

// ── Season Game Popup (read-only, shown when tapping a season game on calendar)
const SeasonGamePopup = ({ game, seasons, venues, members, onClose, onManageSeasons }) => {
  const season = seasons.find((s) => s.id === game.season_id);
  const venue = game.venue_id ? venues.find((v) => v.id === game.venue_id) : null;
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!game?.id) return;
    const load = async () => {
      const { data } = await supabase
        .from("event_photos").select("*").eq("event_id", game.id).order("created_at");
      if (data) {
        const withUrls = await Promise.all(data.map(async (p) => {
          const { data: signed } = await supabase.storage.from("event-photos").createSignedUrl(p.storage_path, 3600);
          return { ...p, url: signed?.signedUrl || "" };
        }));
        setPhotos(withUrls);
      }
    };
    load();
  }, [game?.id]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = `${game.id}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage.from("event-photos").upload(path, file);
      if (uploadError) throw uploadError;
      const { data: row, error: dbError } = await supabase.from("event_photos")
        .insert({ event_id: game.id, storage_path: path }).select().single();
      if (dbError) throw dbError;
      const { data: signed } = await supabase.storage.from("event-photos").createSignedUrl(path, 3600);
      setPhotos((prev) => [...prev, { ...row, url: signed?.signedUrl || "" }]);
    } catch (err) {
      alert("Error uploading photo: " + err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await supabase.storage.from("event-photos").remove([photo.storage_path]);
      await supabase.from("event_photos").delete().eq("id", photo.id);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
    } catch (err) {
      alert("Error deleting photo: " + err.message);
    }
  };
  const venueName = venue?.name || game.location;
  const mapLink = (venue?.address || game.location)
    ? `https://maps.google.com/?q=${encodeURIComponent(venue?.address || game.location)}`
    : null;
  const gameMembers = game.member_ids?.length
    ? members.filter((m) => game.member_ids.includes(m.id))
    : [];

  const fmtDate = (d) => {
    if (!d) return "";
    const [y, mo, dy] = d.split("-").map(Number);
    return new Date(y, mo - 1, dy).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };
  const fmtTime = (t) => {
    if (!t) return null;
    const [h, mn] = t.split(":").map(Number);
    const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hr}:${String(mn).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-3">
          <div className="min-w-0 flex-1">
            {game.canceled && (
              <span className="text-xs font-semibold text-red-500 bg-red-50 px-2 py-0.5 rounded-full mb-1 inline-block">CANCELED</span>
            )}
            <h3 className={`font-bold text-gray-800 ${game.canceled ? "line-through text-gray-400" : ""}`}>{game.title}</h3>
            {season && (
              <p className="text-xs text-gray-400 mt-0.5">{season.sport || season.name} · {game.event_type === "practice" ? "Practice" : "Game"}</p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 touch-manipulation flex-shrink-0 ml-2">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-2 mb-4">
          <p className="text-sm text-gray-700">
            📅 {fmtDate(game.date)}{game.time && !game.all_day ? ` · ${fmtTime(game.time)}` : ""}
          </p>
          <WeatherBadge date={game.date} location={venue?.address || game.location} />
          {venueName && (
            <div className="flex items-start gap-1.5">
              <MapPin size={15} className="text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-gray-700">
                {venueName}
                {mapLink && (
                  <a href={mapLink} target="_blank" rel="noreferrer"
                    className="ml-2 text-blue-500 text-xs underline touch-manipulation">
                    Directions
                  </a>
                )}
              </span>
            </div>
          )}
          {gameMembers.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm text-gray-500">👥</span>
              {gameMembers.map((m) => (
                <span key={m.id} className="flex items-center gap-1 text-sm text-gray-700">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: m.color }} />
                  {m.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Photos */}
        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-3">
            {photos.map((photo) => (
              <div key={photo.id} className="relative aspect-square">
                <img src={photo.url} alt="" className="w-full h-full object-cover rounded-lg cursor-pointer"
                  onClick={() => window.open(photo.url, "_blank")} />
                <button type="button" onClick={() => deletePhoto(photo)}
                  className="absolute top-1 right-1 bg-black bg-opacity-60 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-opacity-80 touch-manipulation">
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        <label className={`flex items-center gap-2 px-3 py-2 mb-3 border-2 border-dashed rounded-lg cursor-pointer text-sm font-medium transition-colors ${
          uploading ? "border-gray-200 text-gray-400 pointer-events-none" : "border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-600"
        }`}>
          <Camera size={16} />
          <span>{uploading ? "Uploading…" : photos.length > 0 ? "Add Another Photo" : "Add Photo"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploading} />
        </label>

        <button
          onClick={onManageSeasons}
          className="w-full py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 touch-manipulation"
        >
          Manage in Seasons →
        </button>
      </div>
    </div>
  );
};

const FamilyCalendar = ({ session }) => {
  const [view, setView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState(() => {
    try { const c = localStorage.getItem(CACHE_KEY); return c ? JSON.parse(c).events || [] : []; } catch { return []; }
  });
  const [members, setMembers] = useState(() => {
    try { const c = localStorage.getItem(CACHE_KEY); return c ? JSON.parse(c).members || [] : []; } catch { return []; }
  });
  const [groups, setGroups] = useState(() => {
    try { const c = localStorage.getItem(CACHE_KEY); return c ? JSON.parse(c).groups || [] : []; } catch { return []; }
  });
  const [vacationBlocks, setVacationBlocks] = useState(() => fromCache(VACATIONS_CACHE_KEY));
  const [showEventModal, setShowEventModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(() => localStorage.getItem(CACHE_KEY) === null);
  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState("family");
  const [currentUserMember, setCurrentUserMember] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [announcement, setAnnouncement] = useState(null);
  const [weekStartsMonday, setWeekStartsMonday] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [reminderPhone, setReminderPhone] = useState("");
  const [anniversaryDate, setAnniversaryDate] = useState("");
  const [showChoreView, setShowChoreView] = useState(false);
  const [chores, setChores] = useState(() => fromCache(CHORES_CACHE_KEY));
  const [choreCompletions, setChoreCompletions] = useState(() => fromCache(CHORE_COMPLETIONS_CACHE_KEY));
  const [showChoreModal, setShowChoreModal] = useState(false);
  const [editingChore, setEditingChore] = useState(null);
  const [showShoppingView, setShowShoppingView] = useState(false);
  const [shoppingItems, setShoppingItems] = useState(() => fromCache(SHOPPING_CACHE_KEY));
  const [externalContacts, setExternalContacts] = useState(() => fromCache(CONTACTS_CACHE_KEY));
  const [seasons, setSeasons] = useState(() => {
    try { const c = localStorage.getItem(SEASONS_CACHE_KEY); return c ? JSON.parse(c) : []; } catch { return []; }
  });
  const [showSeasonsView, setShowSeasonsView] = useState(false);
  const [venues, setVenues] = useState(() => fromCache(VENUES_CACHE_KEY));
  const swRegistrationRef = useRef(null);

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const pullStartY = useRef(null);
  const notificationTimeoutsRef = useRef([]);
  const loadedCenterRef = useRef(null); // { year, month }
  const defaultViewApplied = useRef(false);
  const currentDateRef = useRef(currentDate);

  useEffect(() => { currentDateRef.current = currentDate; }, [currentDate]);


  // Online/offline detection
  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    // Load primary calendar data first, then secondary data one at a time.
    // Sequential secondary loads avoid Supabase connection pool saturation
    // and avoid a burst of simultaneous setState re-renders after the calendar appears.
    let retryTimer = null;
    const runFullLoad = async () => {
      const timedOut = await loadAllData(new Date());
      const loadSecondary = async () => {
        await loadUserPreferences();
        await loadAnnouncement();
        await loadVacationBlocks();
        await loadChores();
        await loadChoreCompletions();
        await loadShoppingItems();
        await loadExternalContacts();
        await loadSeasons();
        await loadVenues();
      };
      await loadSecondary().catch(err => console.error('[FC] Secondary load error:', err));
      if (timedOut) {
        console.log('[FC] Load timed out, retrying in 30s...');
        retryTimer = setTimeout(runFullLoad, 30000);
      }
    };
    runFullLoad();

    registerServiceWorker();

    const channel = supabase
      .channel("db-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "events" }, (payload) => {
        if (payload.eventType === "INSERT") {
          setEvents((prev) =>
            prev.some((e) => e.id === payload.new.id) ? prev : [...prev, payload.new]
          );
        } else if (payload.eventType === "UPDATE") {
          setEvents((prev) => prev.map((e) => (e.id === payload.new.id ? payload.new : e)));
        } else if (payload.eventType === "DELETE") {
          setEvents((prev) => prev.filter((e) => e.id !== payload.old.id));
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "family_members" }, () => loadAllData(currentDateRef.current))
      .on("postgres_changes", { event: "*", schema: "public", table: "family_groups" }, () => loadAllData(currentDateRef.current))
      .on("postgres_changes", { event: "*", schema: "public", table: "vacation_blocks" }, loadVacationBlocks)
      .on("postgres_changes", { event: "*", schema: "public", table: "announcements" }, (payload) => {
        if (payload.eventType === "UPDATE" || payload.eventType === "INSERT") {
          setAnnouncement(payload.new);
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "chores" }, (payload) => {
        if (payload.eventType === "INSERT") {
          setChores((prev) =>
            prev.some((c) => c.id === payload.new.id) ? prev : [...prev, payload.new]
          );
        } else if (payload.eventType === "UPDATE") {
          setChores((prev) => prev.map((c) => (c.id === payload.new.id ? payload.new : c)));
        } else if (payload.eventType === "DELETE") {
          setChores((prev) => prev.filter((c) => c.id !== payload.old.id));
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "shopping_items" }, (payload) => {
        if (payload.eventType === "INSERT") {
          setShoppingItems((prev) =>
            prev.some((i) => i.id === payload.new.id) ? prev : [...prev, payload.new]
          );
        } else if (payload.eventType === "UPDATE") {
          setShoppingItems((prev) => prev.map((i) => (i.id === payload.new.id ? payload.new : i)));
        } else if (payload.eventType === "DELETE") {
          setShoppingItems((prev) => prev.filter((i) => i.id !== payload.old.id));
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "chore_completions" }, (payload) => {
        if (payload.eventType === "INSERT") {
          setChoreCompletions((prev) =>
            prev.some((c) => c.id === payload.new.id) ? prev : [...prev, payload.new]
          );
        } else if (payload.eventType === "DELETE") {
          setChoreCompletions((prev) => prev.filter((c) => c.id !== payload.old.id));
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "seasons" }, (payload) => {
        if (payload.eventType === "INSERT") {
          setSeasons((prev) => prev.some((s) => s.id === payload.new.id) ? prev : [...prev, payload.new]);
        } else if (payload.eventType === "UPDATE") {
          setSeasons((prev) => prev.map((s) => (s.id === payload.new.id ? payload.new : s)));
        } else if (payload.eventType === "DELETE") {
          setSeasons((prev) => prev.filter((s) => s.id !== payload.old.id));
        }
      })
      .subscribe((status, err) => {
        if (err) console.warn("[FC] Realtime channel error:", status, err);
      });

    return () => { supabase.removeChannel(channel); clearTimeout(retryTimer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reschedule notifications whenever events change
  useEffect(() => {
    scheduleNotifications(events);
  }, [events]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy reload: when navigating more than 4 months from loaded center
  useEffect(() => {
    if (!loadedCenterRef.current) return;
    const { year, month } = loadedCenterRef.current;
    const diff =
      Math.abs((currentDate.getFullYear() - year) * 12 + (currentDate.getMonth() - month));
    if (diff > 4) {
      loadEventsWindow(currentDate).then((eventsData) => {
        if (eventsData) {
          setEvents(eventsData);
          loadedCenterRef.current = { year: currentDate.getFullYear(), month: currentDate.getMonth() };
        }
      }).catch((err) => console.error("Error lazy-loading events:", err));
    }
  }, [currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadEventsWindow = async (centerDate) => {
    const { fromStr, toStr } = buildLoadWindow(centerDate);
    const [windowRes, recurringRes] = await Promise.all([
      supabase.from("events").select("*")
        .or(`date.gte.${fromStr},end_date.gte.${fromStr}`)
        .lte("date", toStr)
        .order("date"),
      supabase.from("events").select("*")
        .or("repeat.eq.daily,repeat.eq.weekly,repeat.eq.monthly,repeat.eq.yearly")
        .order("date"),
    ]);
    if (windowRes.error) throw windowRes.error;
    if (recurringRes.error) throw recurringRes.error;
    const seen = new Set();
    const merged = [];
    [...(windowRes.data || []), ...(recurringRes.data || [])].forEach((e) => {
      if (!seen.has(e.id)) { seen.add(e.id); merged.push(e); }
    });
    return merged;
  };

  const loadAllData = async (centerDate = currentDateRef.current) => {
    const hasCache = localStorage.getItem(CACHE_KEY) !== null;
    if (!hasCache) setLoading(true);
    setLoadTimedOut(false);

    const makeTimeout = (ms) => new Promise((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), ms)
    );

    try {
      const [membersRes, groupsRes] = await Promise.race([
        Promise.all([
          supabase.from("family_members").select("*").order("id"),
          supabase.from("family_groups").select("*").order("id"),
        ]),
        makeTimeout(25000),
      ]);
      if (membersRes.error) throw membersRes.error;
      if (groupsRes.error) throw groupsRes.error;

      const eventsData = await Promise.race([loadEventsWindow(centerDate), makeTimeout(25000)]);
      loadedCenterRef.current = { year: centerDate.getFullYear(), month: centerDate.getMonth() };

      setMembers(membersRes.data || []);
      setGroups(groupsRes.data || []);
      setEvents(eventsData);

      const userMember = (membersRes.data || []).find((m) => m.auth_user_id === session?.user?.id);
      setCurrentUserMember(userMember || null);

      setLoadTimedOut(false);
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        members: membersRes.data,
        groups: groupsRes.data,
        events: eventsData,
      }));
    } catch (error) {
      console.error("Error loading data:", error);
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { members: m, groups: g, events: e } = JSON.parse(cached);
          setMembers(m || []);
          setGroups(g || []);
          setEvents(e || []);
          setCurrentUserMember((m || []).find((mem) => mem.auth_user_id === session?.user?.id) || null);
        } catch (_) {}
      }
      if (error?.message === "timeout") {
        setLoadTimedOut(true);
        return true; // signal retry needed
      }
    } finally {
      setLoading(false);
    }
  };

  const cachedLoad = async (cacheKey, query, setter) => {
    try {
      const res = await Promise.race([
        query,
        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 15000)),
      ]);
      if (res.error) throw res.error;
      setter(res.data || []);
      try { localStorage.setItem(cacheKey, JSON.stringify(res.data || [])); } catch (_) {}
    } catch (err) {
      console.error(`Error loading ${cacheKey}:`, err);
      const cached = fromCache(cacheKey);
      if (cached.length) setter(cached);
    }
  };

  const loadVacationBlocks = () => cachedLoad(
    VACATIONS_CACHE_KEY,
    supabase.from("vacation_blocks").select("*").order("start_date"),
    setVacationBlocks
  );

  const loadChores = () => cachedLoad(
    CHORES_CACHE_KEY,
    supabase.from("chores").select("*").order("id"),
    setChores
  );

  const loadChoreCompletions = () => cachedLoad(
    CHORE_COMPLETIONS_CACHE_KEY,
    supabase.from("chore_completions").select("*").order("completed_at"),
    setChoreCompletions
  );

  // ── Shopping list ──────────────────────────────────────────────────────────
  const loadShoppingItems = () => cachedLoad(
    SHOPPING_CACHE_KEY,
    supabase.from("shopping_items").select("*").order("id"),
    setShoppingItems
  );

  const addShoppingItem = async (itemData) => {
    const tempId = Date.now();
    setShoppingItems((prev) => [...prev, { ...itemData, id: tempId, checked: false }]);
    try {
      const { data, error } = await supabase
        .from("shopping_items")
        .insert([itemData])
        .select()
        .single();
      if (error) throw error;
      setShoppingItems((prev) => prev.map((i) => (i.id === tempId ? data : i)));
    } catch (error) {
      console.error("Error adding shopping item:", error);
      setShoppingItems((prev) => prev.filter((i) => i.id !== tempId));
      alert("Error adding item.");
    }
  };

  const checkShoppingItem = async (item) => {
    const updated = { ...item, checked: !item.checked, checked_at: !item.checked ? new Date().toISOString() : null };
    setShoppingItems((prev) => prev.map((i) => (i.id === item.id ? updated : i)));
    try {
      const { error } = await supabase
        .from("shopping_items")
        .update({ checked: !item.checked, checked_at: !item.checked ? new Date().toISOString() : null })
        .eq("id", item.id);
      if (error) throw error;
    } catch (error) {
      console.error("Error updating shopping item:", error);
      loadShoppingItems();
    }
  };

  const deleteShoppingItem = async (itemId) => {
    setShoppingItems((prev) => prev.filter((i) => i.id !== itemId));
    try {
      const { error } = await supabase.from("shopping_items").delete().eq("id", itemId);
      if (error) throw error;
    } catch (error) {
      console.error("Error deleting shopping item:", error);
      loadShoppingItems();
    }
  };

  const clearCheckedItems = async () => {
    const checkedIds = shoppingItems.filter((i) => i.checked).map((i) => i.id);
    if (checkedIds.length === 0) return;
    setShoppingItems((prev) => prev.filter((i) => !i.checked));
    try {
      const { error } = await supabase.from("shopping_items").delete().in("id", checkedIds);
      if (error) throw error;
    } catch (error) {
      console.error("Error clearing checked items:", error);
      loadShoppingItems();
    }
  };

  // ── External contacts + SMS ────────────────────────────────────────────────
  const loadExternalContacts = () => cachedLoad(
    CONTACTS_CACHE_KEY,
    supabase.from("external_contacts").select("*").order("name"),
    setExternalContacts
  );

  const loadVenues = () => cachedLoad(
    VENUES_CACHE_KEY,
    supabase.from("venues").select("*").order("name"),
    setVenues
  );

  const loadSeasons = () => cachedLoad(
    SEASONS_CACHE_KEY,
    supabase.from("seasons").select("*").order("id"),
    setSeasons
  );

  // ── Season SMS helpers ─────────────────────────────────────────────────────
  const smsFormatDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  const smsFormatTime = (timeStr) => {
    if (!timeStr) return null;
    const [h, mn] = timeStr.split(":").map(Number);
    const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? "AM" : "PM";
    return `${hour}:${String(mn).padStart(2, "0")} ${ampm}`;
  };

  // eventType: 'game' | 'practice' — 'games' subscribers only get 'game' events
  const notifySeasonSubscribers = async (seasonId, message, eventType = "game") => {
    try {
      const { data: subs, error } = await supabase
        .from("season_subscriptions")
        .select("external_contact_id, notify_type")
        .eq("season_id", seasonId);
      if (error || !subs?.length) return;
      const matchingIds = subs
        .filter((s) => s.notify_type === "all" || eventType === "game")
        .map((s) => s.external_contact_id);
      const contacts = externalContacts.filter((c) => matchingIds.includes(c.id));
      if (!contacts.length) return;
      const { error: smsError } = await supabase.functions.invoke("send-sms", {
        body: { contacts, rawMessage: message },
      });
      if (smsError) console.error("[SMS] Season notify error:", smsError);
    } catch (err) {
      console.error("Error notifying season subscribers:", err);
    }
  };

  const eventLabel = (event) => event?.event_type === "practice" ? "practice" : "game";

  const generateMapLink = (address) =>
    address ? `https://maps.google.com/?q=${encodeURIComponent(address)}` : null;

  const smsLocationBlock = (location, venueId) => {
    if (!location && !venueId) return "";
    const venue = venueId ? venues.find((v) => v.id === venueId) : null;
    const displayName = venue?.name || location;
    const address = venue?.address || location;
    if (!displayName) return "";
    return `\n📍 ${displayName}`;
  };

  // Returns "Charlie's" based on the season's player, falling back to game members
  const memberPrefix = (game, season) => {
    const ids = season?.player_member_id ? [season.player_member_id]
      : game.member_ids?.length ? game.member_ids
      : game.member_id ? [game.member_id]
      : season?.member_ids?.length ? season.member_ids
      : [];
    const names = ids.map((id) => members.find((m) => m.id === id)?.name).filter(Boolean);
    if (!names.length) return "";
    return names.join(" & ") + "'s";
  };

  const generateGameChangeMessage = (season, oldEvent, newEvent) => {
    const sport = season.sport || season.name;
    const label = eventLabel(newEvent || oldEvent);
    const oldDate = smsFormatDate(oldEvent.date);
    const oldTime = oldEvent.time ? ` at ${smsFormatTime(oldEvent.time)}` : "";
    const newDate = smsFormatDate(newEvent.date);
    const newTime = newEvent.time ? ` at ${smsFormatTime(newEvent.time)}` : "";
    const loc = smsLocationBlock(newEvent.location, newEvent.venue_id);
    return `${sport} ${label} on ${oldDate}${oldTime} has been rescheduled to ${newDate}${newTime}.${loc} — Neary Family Calendar`;
  };

  const generateVenueMessage = (season, event, wasEmpty) => {
    const sport = season.sport || season.name;
    const label = eventLabel(event);
    const dateStr = smsFormatDate(event.date);
    const timeStr = event.time ? ` at ${smsFormatTime(event.time)}` : "";
    const action = wasEmpty ? "Venue set" : "Venue updated";
    const loc = smsLocationBlock(event.location, event.venue_id);
    return `${sport} ${label} on ${dateStr}${timeStr}: ${action}.${loc} — Neary Family Calendar`;
  };

  const generateGameCancelMessage = (season, event, reason) => {
    const prefix = memberPrefix(event, season);
    const sport = season.sport || season.name;
    const label = eventLabel(event);
    const dateStr = smsFormatDate(event.date);
    const timeStr = event.time ? ` at ${smsFormatTime(event.time)}` : "";
    const reasonStr = reason ? ` due to ${reason}` : "";
    const who = prefix ? `${prefix} ` : "";
    return `${who}${sport} ${label} on ${dateStr}${timeStr} has been canceled${reasonStr}. — Neary Family Calendar`;
  };

  const generateGameRestoreMessage = (season, event) => {
    const sport = season.sport || season.name;
    const label = eventLabel(event);
    const dateStr = smsFormatDate(event.date);
    const timeStr = event.time ? ` at ${smsFormatTime(event.time)}` : "";
    const loc = smsLocationBlock(event.location, event.venue_id);
    return `${sport} ${label} on ${dateStr}${timeStr} has been restored. It's back on!${loc} — Neary Family Calendar`;
  };

  const generateGameReminderMessage = (season, event) => {
    const sport = season.sport || season.name;
    const label = eventLabel(event);
    const dateStr = smsFormatDate(event.date);
    const timeStr = event.time ? ` at ${smsFormatTime(event.time)}` : "";
    const loc = smsLocationBlock(event.location, event.venue_id);
    return `Reminder: ${sport} ${label} today — ${event.title} ${dateStr}${timeStr}${loc} — Neary Family Calendar`;
  };

  const sendEventSMS = async (eventData, isUpdate) => {
    if (!eventData.external_contact_ids?.length) return;
    const contacts = externalContacts.filter((c) =>
      eventData.external_contact_ids.map(Number).includes(Number(c.id))
    );
    if (contacts.length === 0) return;
    try {
      const { error } = await supabase.functions.invoke("send-sms", {
        body: { contacts, event: eventData, isUpdate },
      });
      if (error) console.error("[SMS] Error:", error);
    } catch (error) {
      console.error("Error sending SMS:", error);
    }
  };

  // ── Service worker + push notifications ───────────────────────────────────
  const registerServiceWorker = async () => {
    if (!("serviceWorker" in navigator)) return;
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      swRegistrationRef.current = reg;
    } catch (error) {
      console.error("SW registration failed:", error);
    }
  };

  const subscribeToPush = async () => {
    if (!swRegistrationRef.current) return;
    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapidKey) {
      console.warn("REACT_APP_VAPID_PUBLIC_KEY not set — push not available");
      return;
    }
    try {
      const sub = await swRegistrationRef.current.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const { error } = await supabase.from("push_subscriptions").upsert(
        { user_id: session.user.id, member_id: currentUserMember?.id || null, subscription: sub.toJSON() },
        { onConflict: "user_id" }
      );
      if (error) throw error;
    } catch (error) {
      console.error("Push subscription failed:", error);
    }
  };

  const sendPushNotification = async (title, body, tag) => {
    if (!process.env.REACT_APP_VAPID_PUBLIC_KEY) return;
    try {
      await supabase.functions.invoke("send-push", {
        body: { title, body, tag },
      });
    } catch (error) {
      console.error("Failed to send push:", error);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    return new Uint8Array([...rawData].map((c) => c.charCodeAt(0)));
  };

  const addOrUpdateChore = async (choreData) => {
    setShowChoreModal(false);
    if (editingChore) {
      const optimistic = { ...editingChore, ...choreData };
      setChores((prev) => prev.map((c) => (c.id === editingChore.id ? optimistic : c)));
      setEditingChore(null);
      try {
        const { error } = await supabase.from("chores").update(choreData).eq("id", editingChore.id);
        if (error) throw error;
      } catch (error) {
        console.error("Error saving chore:", error);
        alert("Error saving chore.");
        loadChores();
      }
    } else {
      setEditingChore(null);
      try {
        const { error } = await supabase.from("chores").insert([choreData]);
        if (error) throw error;
      } catch (error) {
        console.error("Error adding chore:", error);
        alert("Error adding chore.");
      }
    }
  };

  const deleteChore = async (choreId) => {
    setChores((prev) => prev.filter((c) => c.id !== choreId));
    setShowChoreModal(false);
    setEditingChore(null);
    try {
      const { error } = await supabase.from("chores").delete().eq("id", choreId);
      if (error) throw error;
    } catch (error) {
      console.error("Error deleting chore:", error);
      alert("Error deleting chore.");
      loadChores();
    }
  };

  // ── Season CRUD ───────────────────────────────────────────────────────────
  const addSeason = async (seasonData) => {
    try {
      const { data, error } = await supabase.from("seasons").insert([seasonData]).select().single();
      if (error) throw error;
      setSeasons((prev) => prev.some((s) => s.id === data.id) ? prev : [...prev, data]);
    } catch (error) {
      console.error("Error adding season:", error);
      alert("Error creating season.");
    }
  };

  const deleteSeason = async (seasonId) => {
    setSeasons((prev) => prev.filter((s) => s.id !== seasonId));
    try {
      const { error } = await supabase.from("seasons").delete().eq("id", seasonId);
      if (error) throw error;
    } catch (error) {
      console.error("Error deleting season:", error);
      loadSeasons();
    }
  };

  const addGame = async (gameData) => {
    try {
      const { data, error } = await supabase.from("events").insert([gameData]).select().single();
      if (error) throw error;
      setEvents((prev) => prev.some((e) => e.id === data.id) ? prev : [...prev, data]);
      if (data.season_id) {
        const season = seasons.find((s) => s.id === data.season_id);
        if (season) {
          const evType = data.event_type || "game";
          const label = evType === "practice" ? "Practice" : "Game";
          const dateStr = smsFormatDate(data.date);
          const timeStr = data.time ? ` at ${smsFormatTime(data.time)}` : "";
          const loc = smsLocationBlock(data.location, data.venue_id);
          const msg = `${season.sport || season.name} ${label} added: ${data.title} on ${dateStr}${timeStr}.${loc} — Neary Family Calendar`;
          await notifySeasonSubscribers(data.season_id, msg, evType);
        }
      }
    } catch (error) {
      console.error("Error adding game:", error);
      alert("Error adding game.");
    }
  };

  const saveGame = async (oldGame, newGameData) => {
    const updated = { ...oldGame, ...newGameData };
    setEvents((prev) => prev.map((e) => e.id === oldGame.id ? updated : e));
    try {
      const { error } = await supabase.from("events").update(newGameData).eq("id", oldGame.id);
      if (error) throw error;
      const normTime = (t) => (t || "").substring(0, 5) || null;
      const dateChanged = oldGame.date !== newGameData.date;
      const timeChanged = normTime(oldGame.time) !== normTime(newGameData.time);
      const locationChanged = (oldGame.location || null) !== (newGameData.location || null);
      const wasRescheduled = oldGame.canceled && newGameData.canceled === false;
      if (oldGame.season_id) {
        const season = seasons.find((s) => s.id === oldGame.season_id);
        if (season) {
          const evType = newGameData.event_type || oldGame.event_type || "game";
          const label = evType === "practice" ? "practice" : "game";
          const sport = season.sport || season.name;
          const updatedEvent = { ...oldGame, ...newGameData };
          if (wasRescheduled) {
            const prefix = memberPrefix(oldGame, season);
            const who = prefix ? `${prefix} ` : "";
            const oldDate = smsFormatDate(oldGame.date);
            const oldTime = oldGame.time ? ` at ${smsFormatTime(oldGame.time)}` : "";
            const newDate = smsFormatDate(newGameData.date);
            const newTime = newGameData.time ? ` at ${smsFormatTime(newGameData.time)}` : "";
            const venueName = newGameData.location || venues.find((v) => v.id === Number(newGameData.venue_id))?.name;
            const locStr = venueName ? ` at ${venueName}` : "";
            const msg = `${who}${sport} ${label} originally scheduled for ${oldDate}${oldTime} has been rescheduled. It will now be played on ${newDate}${newTime}${locStr}. The calendar has been updated to reflect this change. — Neary Family Calendar`;
            await notifySeasonSubscribers(oldGame.season_id, msg, evType);
          } else if (dateChanged || timeChanged) {
            await notifySeasonSubscribers(oldGame.season_id, generateGameChangeMessage(season, oldGame, newGameData), evType);
          } else if (locationChanged && newGameData.location) {
            const wasEmpty = !oldGame.location;
            await notifySeasonSubscribers(oldGame.season_id, generateVenueMessage(season, updatedEvent, wasEmpty), evType);
          }
        }
      }
    } catch (error) {
      console.error("Error saving game:", error);
      loadAllData(currentDateRef.current);
    }
  };

  const deleteGame = async (game) => {
    setEvents((prev) => prev.filter((e) => e.id !== game.id));
    try {
      const { error } = await supabase.from("events").delete().eq("id", game.id);
      if (error) throw error;
    } catch (error) {
      console.error("Error deleting game:", error);
      loadAllData(currentDateRef.current);
    }
  };

  const cancelGame = async (game, reason) => {
    setEvents((prev) => prev.map((e) => e.id === game.id ? { ...e, canceled: true } : e));
    try {
      const { error } = await supabase.from("events").update({ canceled: true }).eq("id", game.id);
      if (error) throw error;
      const season = seasons.find((s) => s.id === game.season_id);
      if (season) {
        const msg = generateGameCancelMessage(season, game, reason);
        await notifySeasonSubscribers(game.season_id, msg, game.event_type || "game");
      }
    } catch (error) {
      console.error("Error canceling game:", error);
      loadAllData(currentDateRef.current);
    }
  };

  const restoreGame = async (game) => {
    setEvents((prev) => prev.map((e) => e.id === game.id ? { ...e, canceled: false } : e));
    try {
      const { error } = await supabase.from("events").update({ canceled: false }).eq("id", game.id);
      if (error) throw error;
      const season = seasons.find((s) => s.id === game.season_id);
      if (season) {
        const msg = generateGameRestoreMessage(season, game);
        await notifySeasonSubscribers(game.season_id, msg, game.event_type || "game");
      }
    } catch (error) {
      console.error("Error restoring game:", error);
      loadAllData(currentDateRef.current);
    }
  };

  const handleSeasonSubscribe = async (contact, notifyType, season, upcomingEvents) => {
    // If the contact has email, sendCalendarLink (called right after) will send
    // the full schedule + ICS link via email — no need to also SMS the schedule.
    if (contact.email) return;

    // Phone-only contacts: send welcome SMS with schedule (no URLs)
    const sport = season.sport || season.name;
    const scope = notifyType === "all" ? "games & practices" : "games";
    let msg = `You're subscribed to ${sport} ${scope} updates!\n`;
    const upcoming = upcomingEvents.slice(0, 6);
    if (upcoming.length > 0) {
      msg += `\nUpcoming schedule:\n`;
      upcoming.forEach((ev) => {
        const label = ev.event_type === "practice" ? "Practice" : "Game";
        const dateStr = smsFormatDate(ev.date);
        const timeStr = ev.time ? ` at ${smsFormatTime(ev.time)}` : "";
        const locStr = ev.location ? ` @ ${ev.location}` : "";
        msg += `• ${label}: ${ev.title} \u2014 ${dateStr}${timeStr}${locStr}\n`;
      });
    } else {
      msg += `\nNo upcoming events yet — we'll text you when they're added.`;
    }
    msg += `\n\u2014 Family Calendar`;
    try {
      await supabase.functions.invoke("send-sms", {
        body: { contacts: [contact], rawMessage: msg },
      });
    } catch (err) {
      console.error("Error sending welcome SMS:", err);
    }
  };

  const advanceDueDate = (dueDateStr, repeat) => {
    const base = dueDateStr ? new Date(dueDateStr + "T00:00:00") : new Date();
    if (repeat === "daily") base.setDate(base.getDate() + 1);
    else if (repeat === "weekly") base.setDate(base.getDate() + 7);
    else if (repeat === "monthly") base.setMonth(base.getMonth() + 1);
    return `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(base.getDate()).padStart(2, "0")}`;
  };

  const completeChore = async (chore, memberId) => {
    const now = new Date().toISOString();

    if (chore.repeat) {
      // Recurring: advance due date instead of marking done
      const newDueDate = advanceDueDate(chore.due_date, chore.repeat);
      setChores((prev) =>
        prev.map((c) => (c.id === chore.id ? { ...c, due_date: newDueDate } : c))
      );
      try {
        const [choreRes, completionRes] = await Promise.all([
          supabase.from("chores").update({ due_date: newDueDate }).eq("id", chore.id),
          supabase.from("chore_completions").insert([{
            chore_id: chore.id,
            chore_title: chore.title,
            member_id: memberId || null,
            points: chore.points,
            completed_date: now,
          }]),
        ]);
        if (choreRes.error) throw choreRes.error;
        if (completionRes.error) throw completionRes.error;
      } catch (error) {
        console.error("Error completing chore:", error);
        alert("Error completing chore.");
        loadChores();
      }
    } else {
      // One-off: mark as completed
      setChores((prev) =>
        prev.map((c) =>
          c.id === chore.id
            ? { ...c, completed: true, completed_at: now, completed_by_member_id: memberId }
            : c
        )
      );
      try {
        const [choreRes, completionRes] = await Promise.all([
          supabase.from("chores").update({
            completed: true,
            completed_at: now,
            completed_by_member_id: memberId || null,
          }).eq("id", chore.id),
          supabase.from("chore_completions").insert([{
            chore_id: chore.id,
            chore_title: chore.title,
            member_id: memberId || null,
            points: chore.points,
            completed_date: now,
          }]),
        ]);
        if (choreRes.error) throw choreRes.error;
        if (completionRes.error) throw completionRes.error;
      } catch (error) {
        console.error("Error completing chore:", error);
        alert("Error completing chore.");
        loadChores();
      }
    }
  };

  const uncompleteChore = async (chore) => {
    // Optimistic update
    setChores((prev) =>
      prev.map((c) =>
        c.id === chore.id
          ? { ...c, completed: false, completed_at: null, completed_by_member_id: null }
          : c
      )
    );
    try {
      // Delete the most recent completion record for this chore
      const recentCompletion = [...choreCompletions]
        .filter((c) => c.chore_id === chore.id)
        .sort((a, b) => new Date(b.completed_date) - new Date(a.completed_date))[0];

      const ops = [supabase.from("chores").update({
        completed: false,
        completed_at: null,
        completed_by_member_id: null,
      }).eq("id", chore.id)];

      if (recentCompletion) {
        ops.push(supabase.from("chore_completions").delete().eq("id", recentCompletion.id));
        setChoreCompletions((prev) => prev.filter((c) => c.id !== recentCompletion.id));
      }

      const results = await Promise.all(ops);
      for (const r of results) {
        if (r.error) throw r.error;
      }
    } catch (error) {
      console.error("Error uncompleting chore:", error);
      alert("Error uncompleting chore.");
      loadChores();
      loadChoreCompletions();
    }
  };

  const loadAnnouncement = async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => {
        controller.abort();
        console.warn('[FC] loadAnnouncement timed out');
      }, 5000);
      const { data, error } = await supabase.from("announcements").select("*").eq("id", 1).maybeSingle().abortSignal(controller.signal);
      clearTimeout(timeout);
      if (error) throw error;
      setAnnouncement(data || null);
    } catch (error) {
      if (error?.name !== 'AbortError') console.error("Error loading announcement:", error);
    }
  };

  const saveAnnouncement = async (content) => {
    try {
      const { error } = await supabase.from("announcements").upsert({
        id: 1,
        content,
        updated_at: new Date().toISOString(),
        updated_by_name: currentUserMember?.name || null,
      });
      if (error) throw error;
    } catch (error) {
      console.error("Error saving announcement:", error);
      alert("Error saving note.");
    }
  };

  const loadUserPreferences = async () => {
    if (!session?.user?.id) return;
    try {
      const { data, error } = await supabase
        .from("user_preferences")
        .select("view_mode, default_view, week_starts_monday, is_admin, reminder_phone, anniversary_date")
        .eq("user_id", session.user.id)
        .single();
      if (error && error.code !== "PGRST116") throw error;
      if (data) {
        setViewMode(data.view_mode || "family");
        setWeekStartsMonday(data.week_starts_monday ?? false);
        setIsAdmin(data.is_admin ?? false);
        setReminderPhone(data.reminder_phone || "");
        setAnniversaryDate(data.anniversary_date || "");
        // Always open to month view; do not restore last-used view
      }
    } catch (error) {
      console.error("Error loading user preferences:", error);
    }
  };

  const savePreferences = async (prefs) => {
    if (!session?.user?.id) return;
    try {
      const { error } = await supabase.from("user_preferences").upsert(
        {
          user_id: session.user.id,
          week_starts_monday: prefs.weekStartsMonday,
          default_view: prefs.defaultView,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );
      if (error) throw error;
      setWeekStartsMonday(prefs.weekStartsMonday);
      defaultViewApplied.current = true;
    } catch (error) {
      console.error("Error saving preferences:", error);
      alert("Error saving settings.");
    }
  };

  const saveAdminPrefs = async (prefs) => {
    if (!session?.user?.id) return;
    try {
      const { error } = await supabase.from("user_preferences").upsert(
        {
          user_id: session.user.id,
          is_admin: prefs.isAdmin,
          reminder_phone: prefs.reminderPhone || null,
          anniversary_date: prefs.anniversaryDate || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      );
      if (error) throw error;
      setIsAdmin(prefs.isAdmin);
      setReminderPhone(prefs.reminderPhone || "");
      setAnniversaryDate(prefs.anniversaryDate || "");
    } catch (error) {
      console.error("Error saving admin prefs:", error);
      alert("Error saving admin settings.");
    }
  };

  const changeView = (newView) => {
    setView(newView);
  };

  // ── Birthday & anniversary SMS reminders (admin only) ─────────────────────
  useEffect(() => {
    if (!isAdmin || !reminderPhone) return;

    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const todayStr = new Date().toISOString().slice(0, 10);

    let sent = {};
    try { sent = JSON.parse(localStorage.getItem("fc_special_reminders") || "{}"); } catch {}
    // Prune entries older than 60 days
    const sixtyDaysAgo = now - 60 * 24 * 60 * 60 * 1000;
    Object.keys(sent).forEach(k => { if (sent[k] < sixtyDaysAgo) delete sent[k]; });
    localStorage.setItem("fc_special_reminders", JSON.stringify(sent));

    const getNextOccurrence = (monthDay) => {
      const year = new Date().getFullYear();
      const thisYear = `${year}-${monthDay}`;
      return thisYear >= todayStr ? thisYear : `${year + 1}-${monthDay}`;
    };

    const schedule = (key, targetDateStr, message) => {
      if (sent[key]) return;
      const [y, m, d] = targetDateStr.split("-").map(Number);
      const targetMs = new Date(y, m - 1, d).getTime();
      const fireMs = targetMs - sevenDays;
      if (targetMs < now) return; // event already passed
      const delay = Math.max(0, fireMs - now); // fire now if 7-day mark already passed
      if (delay > 30 * 24 * 60 * 60 * 1000) return; // >30 days out — reschedule on next load
      setTimeout(async () => {
        let current = {};
        try { current = JSON.parse(localStorage.getItem("fc_special_reminders") || "{}"); } catch {}
        if (current[key]) return;
        await supabase.functions.invoke("send-sms", {
          body: { contacts: [{ name: "Admin", phone: reminderPhone }], rawMessage: message },
        });
        current[key] = Date.now();
        localStorage.setItem("fc_special_reminders", JSON.stringify(current));
      }, delay);
    };

    members.forEach(member => {
      if (!member.birthday) return;
      const monthDay = member.birthday.slice(5);
      const next = getNextOccurrence(monthDay);
      schedule(`bday-${member.id}-${next.slice(0, 4)}`, next,
        `🎂 Reminder: ${member.name}'s birthday is in one week (${next})! — Neary Family Calendar`);
    });

    if (anniversaryDate) {
      const monthDay = anniversaryDate.slice(5);
      const next = getNextOccurrence(monthDay);
      schedule(`anniversary-${next.slice(0, 4)}`, next,
        `💍 Reminder: Your wedding anniversary is in one week (${next})! — Neary Family Calendar`);
    }
  }, [isAdmin, reminderPhone, anniversaryDate, members]);

  // ── Notifications ─────────────────────────────────────────────────────────
  const scheduleNotifications = useCallback((evts) => {
    notificationTimeoutsRef.current.forEach(clearTimeout);
    notificationTimeoutsRef.current = [];
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;

    const now = Date.now();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;

    evts.forEach((event) => {
      if (!event.notify_minutes_before || event.all_day || !event.time) return;
      const occurrenceDates = [];
      const [sy, sm, sd] = event.date.split("-").map(Number);
      const startDate = new Date(sy, sm - 1, sd);

      if (startDate.getTime() + sevenDays >= now) occurrenceDates.push(event.date);

      if (event.repeat) {
        const end = new Date(now + sevenDays);
        // Start from today (not the original event date) to avoid iterating years of old dates
        const loopStart = new Date(Math.max(startDate.getTime(), now - 24 * 60 * 60 * 1000));
        const d = new Date(loopStart);
        d.setDate(d.getDate() + 1);
        while (d <= end) {
          if (isRecurringOnDate(event, d)) {
            occurrenceDates.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
          }
          d.setDate(d.getDate() + 1);
        }
      }

      occurrenceDates.forEach((dateStr) => {
        const [ey, em, ed] = dateStr.split("-").map(Number);
        const [eh, emin] = event.time.split(":").map(Number);
        const eventTime = new Date(ey, em - 1, ed, eh, emin).getTime();
        const delay = eventTime - event.notify_minutes_before * 60000 - now;
        if (delay > 0 && delay < sevenDays) {
          const label = event.notify_minutes_before >= 60
            ? `${event.notify_minutes_before / 60}h`
            : `${event.notify_minutes_before}min`;
          const notifTitle = event.title;
          const notifBody = `Starting in ${label} at ${event.time.substring(0, 5)}`;
          const notifTag = `event-${event.id}-${dateStr}`;
          const id = setTimeout(() => {
            // In-app notification (when app is open)
            if (Notification.permission === "granted") {
              new Notification(notifTitle, {
                body: notifBody,
                icon: "/logo192.png",
                tag: notifTag,
              });
            }
            // Real push to all subscribed family devices
            sendPushNotification(notifTitle, notifBody, notifTag);
          }, delay);
          notificationTimeoutsRef.current.push(id);
        }
      });
    });
  }, []);

  const requestNotificationPermission = async () => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      await Notification.requestPermission();
      scheduleNotifications(events);
    }
    if (Notification.permission === "granted") {
      await subscribeToPush();
    }
  };

  // ── SMS Reminders ─────────────────────────────────────────────────────────
  const sendGameReminderSms = async (game, season) => {
    await notifySeasonSubscribers(
      season.id,
      generateGameReminderMessage(season, game),
      game.event_type || "game"
    );
  };

  const handleBulkAdded = async (count, season) => {
    const msg = `${count} new ${season.sport || season.name} event${count !== 1 ? "s" : ""} added to the schedule. — Neary Family Calendar`;
    await notifySeasonSubscribers(season.id, msg);
    await loadAllData(currentDateRef.current);
  };

  const buildScheduleEmailHtml = (contact, season, upcomingEvents, notifyType = "games") => {
    const calName = `${season.name}${season.sport ? ` (${season.sport})` : ""}`;
    const icsToken = notifyType === "all" ? `${season.id}a` : `${season.id}`;
    const icsUrl = `${(process.env.REACT_APP_SUPABASE_URL || "").replace(/^http:\/\//, "https://")}/functions/v1/season-ics?s=${icsToken}`;
    const webcalUrl = icsUrl.replace(/^https:\/\//, "webcal://");

    const rows = upcomingEvents.map((ev) => {
      const typeLabel = ev.event_type === "practice" ? "Practice" : "Game";
      const dateStr = smsFormatDate(ev.date);
      const timeStr = ev.time ? ` at ${smsFormatTime(ev.time)}` : "";
      const locStr = ev.location ? ev.location : "—";
      return `<tr>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;color:#6b7280;font-size:13px;">${typeLabel}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:13px;">${ev.title || typeLabel}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:13px;white-space:nowrap;">${dateStr}${timeStr}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f0f0;font-size:13px;color:#6b7280;">${locStr}</td>
      </tr>`;
    }).join("");

    const scheduleBlock = upcomingEvents.length > 0
      ? `<table style="width:100%;border-collapse:collapse;margin:16px 0;">
          <thead><tr style="background:#f9fafb;">
            <th style="padding:8px 10px;text-align:left;font-size:12px;color:#6b7280;font-weight:600;">TYPE</th>
            <th style="padding:8px 10px;text-align:left;font-size:12px;color:#6b7280;font-weight:600;">EVENT</th>
            <th style="padding:8px 10px;text-align:left;font-size:12px;color:#6b7280;font-weight:600;">WHEN</th>
            <th style="padding:8px 10px;text-align:left;font-size:12px;color:#6b7280;font-weight:600;">LOCATION</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>`
      : `<p style="color:#6b7280;font-style:italic;">No upcoming events yet — we'll email you when they're added.</p>`;

    return `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:620px;margin:0 auto;padding:24px 16px;">
      <h2 style="color:#7c3aed;margin:0 0 4px;">${calName}</h2>
      <p style="color:#6b7280;margin:0 0 20px;font-size:14px;">Upcoming schedule</p>
      <p style="font-size:15px;">Hi ${contact.name}! Here's the upcoming ${calName} schedule.</p>
      ${scheduleBlock}
      <div style="margin-top:28px;padding:20px;background:#f5f3ff;border-radius:10px;">
        <p style="margin:0 0 8px;font-size:14px;font-weight:600;color:#5b21b6;">Subscribe to calendar</p>
        <p style="margin:0 0 10px;font-size:13px;color:#6b7280;">To stay synced automatically as games are added or changed, subscribe using the steps below.</p>
        <p style="margin:0 0 6px;font-size:13px;color:#374151;font-weight:600;">iPhone / iPad</p>
        <p style="margin:0 0 10px;font-size:13px;color:#6b7280;">Open the Calendar app → tap Calendars (bottom) → Add Calendar → Add Subscription Calendar → paste the link below.</p>
        <p style="margin:0 0 6px;font-size:13px;color:#374151;font-weight:600;">Google Calendar</p>
        <p style="margin:0 0 14px;font-size:13px;color:#6b7280;">Go to calendar.google.com → Other calendars → From URL → paste the link below.</p>
        <a href="${webcalUrl}" style="display:inline-block;padding:10px 22px;background:#7c3aed;color:#fff;text-decoration:none;border-radius:7px;font-size:14px;font-weight:600;">Subscribe to Calendar</a>
        <p style="margin:10px 0 0;font-size:11px;color:#9ca3af;">Subscription link: ${icsUrl}</p>
      </div>
      <p style="margin-top:24px;font-size:12px;color:#9ca3af;">— Neary Family Calendar</p>
    </div>`;
  };

  const sendCalendarLink = async (contact, season, notifyType = "games") => {
    const gamesOnly = notifyType !== "all";
    if (!contact.email) {
      // Phone-only fallback: try SMS with shortened URL
      const icsToken = notifyType === "all" ? `${season.id}a` : `${season.id}`;
      const icsUrl = `${(process.env.REACT_APP_SUPABASE_URL || "").replace(/^http:\/\//, "https://")}/functions/v1/season-ics?s=${icsToken}`;
      const calName = `${season.name}${season.sport ? ` (${season.sport})` : ""}`;
      const message = `Hi ${contact.name}! Tap to add the ${calName} schedule to your calendar: {CALENDAR_URL}`;
      await supabase.functions.invoke("send-sms", {
        body: { contacts: [contact], rawMessage: message, urlToShorten: icsUrl },
      });
      return;
    }

    const calName = `${season.name}${season.sport ? ` (${season.sport})` : ""}`;
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = events
      .filter((e) => e.season_id === season.id && !e.canceled && e.date >= today && (!gamesOnly || e.event_type !== "practice"))
      .sort((a, b) => (a.date > b.date ? 1 : -1))
      .slice(0, 25);

    const html = buildScheduleEmailHtml(contact, season, upcoming, notifyType);
    const { data, error } = await supabase.functions.invoke("send-email", {
      body: {
        contacts: [contact],
        subject: `${calName} Schedule`,
        html,
      },
    });
    if (error || data?.failed > 0) {
      console.error("[sendCalendarLink] email failed:", error || data);
      alert(`Failed to send schedule email: ${error?.message || JSON.stringify(data)}`);
    }
  };

  // ── Virtual events (birthdays + vacations) ────────────────────────────────
  const getVirtualEvents = () => {
    const virtuals = [];

    // Birthday events — use repeat: yearly so isRecurringOnDate handles future years
    members.forEach((member) => {
      if (!member.birthday) return;
      virtuals.push({
        id: `bday-${member.id}`,
        title: `🎂 ${member.name}'s Birthday`,
        date: member.birthday,
        all_day: true,
        member_id: member.id,
        repeat: "yearly",
        _virtual: true,
      });
    });

    // Anniversary
    if (anniversaryDate) {
      virtuals.push({
        id: "anniversary",
        title: "💍 Anniversary",
        date: anniversaryDate,
        all_day: true,
        repeat: "yearly",
        _virtual: true,
      });
    }

    // Trip / away blocks
    vacationBlocks.forEach((block) => {
      const ids = block.member_ids || (block.member_id ? [block.member_id] : []);
      const tripMembers = ids.map((id) => members.find((m) => m.id === id)).filter(Boolean);
      const names = tripMembers.map((m) => m.name).join(", ");
      const typeIcon = block.trip_type === "work" ? "✈" : "🏖";
      virtuals.push({
        id: `vac-${block.id}`,
        title: `${typeIcon} ${names || "?"} — ${block.label || "Away"}`,
        date: block.start_date,
        end_date: block.end_date,
        all_day: true,
        member_id: ids[0] || block.member_id,
        _virtual: true,
      });
    });

    return virtuals;
  };

  // ── Filtering & colors ────────────────────────────────────────────────────
  const toggleViewMode = async () => {
    const newMode = viewMode === "family" ? "personal" : "family";
    setViewMode(newMode);
    try {
      const { error } = await supabase.from("user_preferences").upsert(
        { user_id: session.user.id, view_mode: newMode, updated_at: new Date().toISOString() },
        { onConflict: "user_id" }
      );
      if (error) throw error;
    } catch (error) {
      console.error("Error saving view mode:", error);
      setViewMode(viewMode);
    }
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) alert("Error signing out");
  };

  const getFilteredEvents = () => {
    const all = [...events, ...getVirtualEvents()];
    if (viewMode === "family") return all;
    if (!currentUserMember) return [];
    // Members with no auth account are kids — their events appear for all logged-in parents
    const kidIds = new Set(members.filter((m) => !m.auth_user_id).map((m) => m.id));
    return all.filter((event) => {
      if (event.member_id === currentUserMember.id) return true;
      if (event.member_ids && event.member_ids.includes(currentUserMember.id)) return true;
      if (event.group_id) {
        const group = groups.find((g) => g.id === event.group_id);
        if (group && group.member_ids?.includes(currentUserMember.id)) return true;
      }
      // Kid events — show for all parents
      if (event.member_id && kidIds.has(event.member_id)) return true;
      if (event.member_ids?.some((id) => kidIds.has(id))) return true;
      return false;
    });
  };

  const getEventsForDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const dateStr = `${y}-${m}-${d}`;
    return getFilteredEvents().filter((event) => {
      const endDate = event.end_date || event.date;
      if (event.date <= dateStr && dateStr <= endDate) return true;
      if (event.repeat && isRecurringOnDate(event, date)) return true;
      return false;
    });
  };

  const getColorForEvent = (event) => {
    if (event.canceled) {
      return "repeating-linear-gradient(-45deg, #ef4444 0px, #ef4444 5px, #ffffff 5px, #ffffff 10px)";
    }
    if (event.group_id) {
      const group = groups.find((g) => g.id === event.group_id);
      if (group?.member_ids) {
        const colors = group.member_ids
          .map((id) => members.find((m) => m.id === (typeof id === "string" ? parseInt(id) : id))?.color)
          .filter(Boolean);
        if (colors.length > 0) return createStripedGradient(colors);
      }
      return "#6b7280";
    }
    if (event.member_ids?.length > 0) {
      const colors = event.member_ids
        .map((id) => members.find((m) => m.id === (typeof id === "string" ? parseInt(id) : id))?.color)
        .filter(Boolean);
      if (colors.length > 0) return createStripedGradient(colors);
    }
    if (event.member_id) {
      return members.find((m) => m.id === event.member_id)?.color || "#6b7280";
    }
    return "#6b7280";
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const navigate = (dir) => {
    setCurrentDate((d) => {
      const n = new Date(d);
      if (view === "month") n.setMonth(d.getMonth() + dir);
      else if (view === "week" || view === "weekend") n.setDate(d.getDate() + dir * 7);
      else if (view === "day") n.setDate(d.getDate() + dir);
      else if (view === "agenda") n.setDate(d.getDate() + dir * 30);
      return n;
    });
  };

  // Swipe gesture (calendar grid)
  const handleTouchStart = () => {};
  const handleTouchEnd = () => {};

  // Pull-to-refresh
  const handlePullStart = (e) => {
    if (window.scrollY === 0) {
      pullStartY.current = e.touches[0].clientY;
    }
  };
  const handlePullEnd = async (e) => {
    if (pullStartY.current === null) return;
    const diff = e.changedTouches[0].clientY - pullStartY.current;
    pullStartY.current = null;
    if (diff > 80 && !loading) {
      setRefreshing(true);
      await loadAllData(currentDateRef.current);
      await loadVacationBlocks();
      setRefreshing(false);
    }
  };

  // ── Jump to today detection ───────────────────────────────────────────────
  const isViewingCurrent = () => {
    const today = new Date();
    if (view === "day") return currentDate.toDateString() === today.toDateString();
    if (view === "month") return currentDate.getFullYear() === today.getFullYear() && currentDate.getMonth() === today.getMonth();
    if (view === "week") {
      const start = new Date(currentDate);
      const off = weekStartsMonday ? (currentDate.getDay() + 6) % 7 : currentDate.getDay();
      start.setDate(currentDate.getDate() - off);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return today >= start && today <= end;
    }
    if (view === "weekend") {
      const sat = new Date(currentDate);
      sat.setDate(currentDate.getDate() + ((6 - currentDate.getDay() + 7) % 7));
      const sun = new Date(sat);
      sun.setDate(sat.getDate() + 1);
      return today.toDateString() === sat.toDateString() || today.toDateString() === sun.toDateString();
    }
    return true;
  };

  const jumpToToday = () => {
    setCurrentDate(new Date());
    changeView("day");
  };

  // ── Event CRUD ────────────────────────────────────────────────────────────
  const addOrUpdateEvent = async (eventData) => {
    if (eventData.notify_minutes_before) await requestNotificationPermission();

    const existingId = eventData.id || editingEvent?.id;

    if (existingId) {
      const { id, ...updateData } = eventData; // strip id from the update payload
      const existing = editingEvent || events.find((e) => e.id === existingId);
      const optimistic = { ...(existing || {}), ...updateData, id: existingId };
      setEvents((prev) => prev.map((e) => (e.id === existingId ? optimistic : e)));
      setShowEventModal(false);
      setEditingEvent(null);
      setSelectedDate(null);
      setSelectedTime(null);
      try {
        const { error } = await supabase.from("events").update(updateData).eq("id", existingId);
        if (error) throw error;
        sendEventSMS(eventData, true);
        // Notify season subscribers if date/time changed
        const seasonIdForEvent = existing?.season_id || updateData.season_id;
        if (seasonIdForEvent) {
          const season = seasons.find((s) => s.id === seasonIdForEvent);
          if (season) {
            const dateChanged = existing?.date !== updateData.date;
            const timeChanged = (existing?.time || null) !== (updateData.time || null);
            if (dateChanged || timeChanged) {
              const msg = generateGameChangeMessage(season, existing || {}, updateData);
              await notifySeasonSubscribers(seasonIdForEvent, msg);
            }
          }
        }
      } catch (error) {
        console.error("Error saving event:", error);
        alert("Error saving event.");
        loadAllData(currentDateRef.current);
      }
    } else {
      setShowEventModal(false);
      setEditingEvent(null);
      setSelectedDate(null);
      setSelectedTime(null);
      try {
        const { data, error } = await supabase.from("events").insert([eventData]).select().single();
        if (error) throw error;
        setEvents((prev) => prev.some((e) => e.id === data.id) ? prev : [...prev, data]);
        sendEventSMS(eventData, false);
      } catch (error) {
        console.error("Error saving event:", error);
        alert("Error saving event.");
      }
    }
  };

  const duplicateEvent = async (eventData) => {
    setShowEventModal(false);
    setEditingEvent(null);
    setSelectedDate(null);
    setSelectedTime(null);
    try {
      const { error } = await supabase.from("events").insert([eventData]);
      if (error) throw error;
    } catch (error) {
      console.error("Error duplicating event:", error);
      alert("Error duplicating event.");
    }
  };

  const deleteEvent = async (eventId) => {
    setEvents((prev) => prev.filter((e) => e.id !== eventId));
    setShowEventModal(false);
    setEditingEvent(null);
    try {
      const { error } = await supabase.from("events").delete().eq("id", eventId);
      if (error) throw error;
    } catch (error) {
      console.error("Error deleting event:", error);
      alert("Error deleting event.");
      loadAllData(currentDateRef.current);
    }
  };

  const handleRescheduleEvent = async (event, newDate, newStartTime) => {
    const parseMin = (t) => { if (!t) return null; const [h, m] = t.split(":").map(Number); return h * 60 + m; };
    const startMin = parseMin(event.time) ?? 9 * 60;
    const endMin = parseMin(event.end_time) ?? startMin + 60;
    const duration = endMin - startMin;
    const newStartMin = parseMin(newStartTime);
    const newEndMin = Math.min(newStartMin + duration, 24 * 60 - 1);
    const eh = Math.floor(newEndMin / 60);
    const em = newEndMin % 60;
    const newEndTime = `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
    const newDateStr = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, "0")}-${String(newDate.getDate()).padStart(2, "0")}`;

    const updated = { ...event, date: newDateStr, time: newStartTime, end_time: newEndTime };
    setEvents((prev) => prev.map((e) => (e.id === event.id ? updated : e)));
    try {
      const { error } = await supabase.from("events").update({ date: newDateStr, time: newStartTime, end_time: newEndTime }).eq("id", event.id);
      if (error) throw error;
    } catch (error) {
      console.error("Error rescheduling:", error);
      alert("Error rescheduling event.");
      loadAllData(currentDateRef.current);
    }
  };

  // ── Callbacks for views ───────────────────────────────────────────────────
  const handleNavigateToDay = (date) => { setCurrentDate(date); changeView("day"); };
  const handleOpenNewEvent = (date, time = null) => {
    console.log('[FC] handleOpenNewEvent called');
    setSelectedDate(date);
    setSelectedTime(time);
    setEditingEvent(null);
    setShowEventModal(true);
  };
  const [selectedSeasonGame, setSelectedSeasonGame] = useState(null);

  const handleOpenEditEvent = (event) => {
    if (event._virtual) return;
    if (event.season_id) {
      setSelectedSeasonGame(event);
      return;
    }
    setEditingEvent(event);
    setShowEventModal(true);
  };

  // ── Smart header label ────────────────────────────────────────────────────
  const getHeaderLabel = () => {
    const short = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    if (view === "day") {
      return `${dayNames[currentDate.getDay()]}, ${short[currentDate.getMonth()]} ${currentDate.getDate()}`;
    }
    if (view === "week") {
      const d = new Date(currentDate);
      const offset = weekStartsMonday ? (d.getDay() + 6) % 7 : d.getDay();
      const start = new Date(d);
      start.setDate(d.getDate() - offset);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      const startStr = `${short[start.getMonth()]} ${start.getDate()}`;
      const endStr = start.getMonth() === end.getMonth() ? `${end.getDate()}` : `${short[end.getMonth()]} ${end.getDate()}`;
      return `${startStr} – ${endStr}, ${start.getFullYear()}`;
    }
    if (view === "weekend") {
      const d = new Date(currentDate);
      const sat = new Date(d);
      sat.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7));
      const sun = new Date(sat);
      sun.setDate(sat.getDate() + 1);
      return `${short[sat.getMonth()]} ${sat.getDate()} – ${short[sun.getMonth()]} ${sun.getDate()}`;
    }
    if (view === "agenda") {
      return `Next 30 days from ${short[currentDate.getMonth()]} ${currentDate.getDate()}`;
    }
    return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  const saveMembers = async () => { try { await loadAllData(currentDateRef.current); } catch (e) { console.error(e); } };
  const saveGroups = async () => { try { await loadAllData(currentDateRef.current); } catch (e) { console.error(e); } };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-lg sm:text-xl text-gray-600 mb-4">
            {loadTimedOut ? "Taking longer than usual…" : "Loading your family calendar..."}
          </div>
          {loadTimedOut && (
            <button
              onClick={() => loadAllData(currentDateRef.current)}
              className="px-5 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 touch-manipulation"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  const showingCurrent = isViewingCurrent();

  // ── Shopping view (full-screen replacement) ───────────────────────────────
  if (showShoppingView) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-2 sm:p-4">
        <div className="max-w-7xl mx-auto">
          {isOffline && (
            <div className="bg-orange-100 border border-orange-300 text-orange-800 text-sm px-4 py-2 rounded-lg mb-3 flex items-center gap-2">
              <span>⚠️</span><span>Offline — showing cached data</span>
            </div>
          )}

          {/* Shopping header */}
          <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6 mb-3 sm:mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="text-blue-600" size={24} />
                <h1 className="text-lg sm:text-2xl font-bold text-gray-800">Shopping List</h1>
                {shoppingItems.filter((i) => !i.checked).length > 0 && (
                  <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {shoppingItems.filter((i) => !i.checked).length} items
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowShoppingView(false)}
                className="p-2 sm:px-3 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-1 text-sm touch-manipulation"
              >
                <Calendar size={18} />
                <span className="hidden sm:inline">Calendar</span>
              </button>
            </div>
          </div>

          {/* Shopping list */}
          <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
            <ShoppingListView
              items={shoppingItems}
              members={members}
              onAdd={addShoppingItem}
              onCheck={checkShoppingItem}
              onDelete={deleteShoppingItem}
              onClearChecked={clearCheckedItems}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Chore view (full-screen replacement) ──────────────────────────────────
  if (showChoreView) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-2 sm:p-4">
        <div className="max-w-7xl mx-auto">
          {isOffline && (
            <div className="bg-orange-100 border border-orange-300 text-orange-800 text-sm px-4 py-2 rounded-lg mb-3 flex items-center gap-2">
              <span>⚠️</span><span>Offline — showing cached data</span>
            </div>
          )}

          {/* Chore header */}
          <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6 mb-3 sm:mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClipboardList className="text-green-600" size={24} />
                <h1 className="text-lg sm:text-2xl font-bold text-gray-800">Chore List</h1>
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={() => setShowChoreView(false)}
                  className="p-2 sm:px-3 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-1 text-sm touch-manipulation"
                >
                  <Calendar size={18} />
                  <span className="hidden sm:inline">Calendar</span>
                </button>
                <button
                  onClick={() => { setEditingChore(null); setShowChoreModal(true); }}
                  className="py-2 px-3 sm:px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 touch-manipulation text-sm font-medium"
                >
                  <Plus size={18} />
                  <span>Add Chore</span>
                </button>
              </div>
            </div>
          </div>

          {/* Chore list */}
          <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
            <ChoreListView
              chores={chores}
              completions={choreCompletions}
              members={members}
              onEditChore={(chore) => { setEditingChore(chore); setShowChoreModal(true); }}
              onCompleteChore={completeChore}
              onUncompleteChore={uncompleteChore}
            />
          </div>

          {showChoreModal && (
            <ChoreModal
              onClose={() => { setShowChoreModal(false); setEditingChore(null); }}
              onSave={addOrUpdateChore}
              onDelete={deleteChore}
              chore={editingChore}
              members={members}
            />
          )}
        </div>
      </div>
    );
  }

  // ── Seasons view (full-screen replacement) ────────────────────────────────
  if (showSeasonsView) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-2 sm:p-4">
        <div className="max-w-7xl mx-auto">
          {isOffline && (
            <div className="bg-orange-100 border border-orange-300 text-orange-800 text-sm px-4 py-2 rounded-lg mb-3 flex items-center gap-2">
              <span>⚠️</span><span>Offline — showing cached data</span>
            </div>
          )}
          <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6 mb-3 sm:mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="text-yellow-500" size={24} />
                <h1 className="text-lg sm:text-2xl font-bold text-gray-800">Seasons</h1>
                {seasons.length > 0 && (
                  <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                    {seasons.length}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowSeasonsView(false)}
                className="p-2 sm:px-3 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-1 text-sm touch-manipulation"
              >
                <Calendar size={18} />
                <span className="hidden sm:inline">Calendar</span>
              </button>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-3 sm:p-6">
            <SeasonsView
              seasons={seasons}
              externalContacts={externalContacts}
              venues={venues}
              events={events}
              members={members}
              onSeasonCreate={addSeason}
              onSeasonDelete={deleteSeason}
              onAddGame={addGame}
              onSaveGame={saveGame}
              onCancelGame={cancelGame}
              onRestoreGame={restoreGame}
              onDeleteGame={deleteGame}
              onSubscribe={handleSeasonSubscribe}
              onSendReminder={sendGameReminderSms}
              onSendCalendarLink={sendCalendarLink}
              onBulkAdded={handleBulkAdded}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-2 sm:p-4"
      onTouchStart={handlePullStart}
      onTouchEnd={handlePullEnd}
    >
      <div className="max-w-7xl mx-auto">

        {/* Offline banner */}
        {isOffline && (
          <div className="no-print bg-orange-100 border border-orange-300 text-orange-800 text-sm px-4 py-2 rounded-lg mb-3 flex items-center gap-2">
            <span>⚠️</span><span>Offline — showing cached data</span>
          </div>
        )}

        {/* Pull-to-refresh indicator */}
        {refreshing && (
          <div className="no-print flex items-center justify-center gap-2 text-purple-600 text-sm mb-3">
            <RefreshCw size={16} className="animate-spin" />
            <span>Refreshing...</span>
          </div>
        )}

        {/* Header */}
        <div className="no-print bg-white rounded-lg shadow-lg p-3 sm:p-6 mb-3 sm:mb-6">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="text-purple-600" size={24} />
              <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800">Family Calendar</h1>
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              {view === "month" && (
                <button onClick={() => window.print()} className="p-2 sm:px-3 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-1" title="Print calendar">
                  <Printer size={18} />
                  <span className="hidden md:inline text-sm">Print</span>
                </button>
              )}
              <button onClick={handleSignOut} className="p-2 sm:px-3 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex items-center gap-1" title="Sign Out">
                <LogOut size={18} />
                <span className="hidden md:inline text-sm">Sign Out</span>
              </button>
              <button onClick={() => setShowShoppingView(true)} className="p-2 sm:px-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1" title="Shopping List">
                <ShoppingCart size={18} />
                <span className="hidden md:inline text-sm">Shopping</span>
              </button>
              <button onClick={() => setShowChoreView(true)} className="p-2 sm:px-3 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1" title="Chore List">
                <ClipboardList size={18} />
                <span className="hidden md:inline text-sm">Chores</span>
              </button>
              <button onClick={() => setShowSeasonsView(true)} className="p-2 sm:px-3 sm:py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center gap-1" title="Seasons">
                <Trophy size={18} />
                <span className="hidden md:inline text-sm">Seasons</span>
              </button>
              <button onClick={() => setShowSettingsModal(true)} className="p-2 sm:px-3 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-1" title="Manage Family">
                <Users size={18} />
                <span className="hidden md:inline text-sm">Manage Family</span>
              </button>
            </div>
          </div>

          {/* Announcement */}
          <Announcement data={announcement} onSave={saveAnnouncement} />

          {/* View toggles */}
          <div className="mb-3 sm:mb-4">
            <div className="grid grid-cols-5 gap-1 sm:gap-2">
              {[
                { label: "Day", id: "day", action: () => { setCurrentDate(new Date()); changeView("day"); } },
                { label: "Week", id: "week", action: () => changeView("week") },
                { label: "Weekend", id: "weekend", action: () => changeView("weekend") },
                { label: "Month", id: "month", action: () => changeView("month") },
                { label: "Agenda", id: "agenda", action: () => changeView("agenda") },
              ].map(({ label, id, action }) => (
                <button key={id} onClick={action}
                  className={`py-2 px-1 sm:px-4 rounded-lg text-xs sm:text-sm font-medium ${view === id ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-700"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Personal/Family toggle */}
          <div className="mb-3 sm:mb-4 flex items-center justify-center gap-3">
            <button onClick={toggleViewMode}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                viewMode === "personal" ? "bg-blue-100 text-blue-700 hover:bg-blue-200" : "bg-purple-100 text-purple-700 hover:bg-purple-200"
              }`}>
              <span className="text-base">{viewMode === "personal" ? "👤" : "👥"}</span>
              <span>{viewMode === "personal" ? "My Events" : "Family Events"}</span>
            </button>
            {viewMode === "personal" && !currentUserMember && (
              <span className="text-xs text-orange-600">Link your account in settings</span>
            )}
          </div>

          {/* Color legend */}
          {members.length > 0 && (
            <div className="mb-3 sm:mb-4 flex flex-wrap gap-x-4 gap-y-1 justify-center">
              {members.map((member) => (
                <div key={member.id} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: member.color }} />
                  <span className="text-xs text-gray-600">{member.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* Navigation + Add Event */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="flex items-center justify-center gap-2 sm:gap-4 order-2 sm:order-1">
              <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg touch-manipulation" aria-label="Previous">
                <ChevronLeft size={24} />
              </button>
              <div className="text-center">
                <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-800 min-w-[140px] sm:min-w-[200px]">
                  {getHeaderLabel()}
                </h2>
                {!showingCurrent && (
                  <button onClick={jumpToToday}
                    className="text-xs text-purple-600 hover:text-purple-800 font-medium mt-0.5 touch-manipulation">
                    ← Today
                  </button>
                )}
              </div>
              <button onClick={() => navigate(1)} className="p-2 hover:bg-gray-100 rounded-lg touch-manipulation" aria-label="Next">
                <ChevronRight size={24} />
              </button>
            </div>
            <button onClick={() => handleOpenNewEvent(new Date())}
              className="w-full sm:w-auto py-3 sm:py-2 px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 touch-manipulation order-1 sm:order-2 text-sm sm:text-base font-medium">
              <Plus size={18} />
              <span>Add Event</span>
            </button>
          </div>
        </div>

        {/* This Week at a Glance */}
        {!showChoreView && !showShoppingView && !showSeasonsView && (
          <div className="no-print"><WeekAtAGlance
            events={events}
            chores={chores}
            shoppingItems={shoppingItems}
            vacationBlocks={vacationBlocks}
            seasons={seasons}
            onGoToChores={() => setShowChoreView(true)}
            onGoToShopping={() => setShowShoppingView(true)}
            onGoToDate={(dateStr) => {
              const [y, m, d] = dateStr.split("-").map(Number);
              setCurrentDate(new Date(y, m - 1, d));
              changeView("week");
            }}
          /></div>
        )}

        {/* Calendar Grid */}
        <div
          className="bg-white rounded-lg shadow-lg p-3 sm:p-6 print-calendar"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {view === "month" && (
            <h2 className="print-only hidden text-xl font-bold text-gray-800 mb-3">
              {currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })} — Neary Family Calendar
            </h2>
          )}
          {view === "month" && (
            <MonthView
              currentDate={currentDate}
              events={getFilteredEvents()}
              getEventsForDate={getEventsForDate}
              getColorForEvent={getColorForEvent}
              onNavigateToDay={handleNavigateToDay}
              onOpenNewEvent={handleOpenNewEvent}
              onOpenEditEvent={handleOpenEditEvent}
              weekStartsMonday={weekStartsMonday}
            />
          )}
          {view === "week" && (
            <WeekView
              currentDate={currentDate}
              getEventsForDate={getEventsForDate}
              getColorForEvent={getColorForEvent}
              onOpenNewEvent={handleOpenNewEvent}
              onOpenEditEvent={handleOpenEditEvent}
              onRescheduleEvent={handleRescheduleEvent}
              weekStartsMonday={weekStartsMonday}
            />
          )}
          {view === "day" && (
            <DayView
              currentDate={currentDate}
              getEventsForDate={getEventsForDate}
              getColorForEvent={getColorForEvent}
              onOpenNewEvent={handleOpenNewEvent}
              onOpenEditEvent={handleOpenEditEvent}
              onRescheduleEvent={handleRescheduleEvent}
            />
          )}
          {view === "weekend" && (
            <WeekendView
              currentDate={currentDate}
              getEventsForDate={getEventsForDate}
              getColorForEvent={getColorForEvent}
              onOpenNewEvent={handleOpenNewEvent}
              onOpenEditEvent={handleOpenEditEvent}
              onRescheduleEvent={handleRescheduleEvent}
            />
          )}
          {view === "agenda" && (
            <AgendaView
              currentDate={currentDate}
              getEventsForDate={getEventsForDate}
              getColorForEvent={getColorForEvent}
              onOpenEditEvent={handleOpenEditEvent}
              onNavigateToDay={handleNavigateToDay}
            />
          )}
        </div>

        {selectedSeasonGame && (
          <SeasonGamePopup
            game={selectedSeasonGame}
            seasons={seasons}
            venues={venues}
            members={members}
            onClose={() => setSelectedSeasonGame(null)}
            onManageSeasons={() => { setSelectedSeasonGame(null); setShowSeasonsView(true); }}
          />
        )}

        {showEventModal && (
          <EventModal
            onClose={() => { setShowEventModal(false); setEditingEvent(null); setSelectedDate(null); setSelectedTime(null); }}
            onSave={addOrUpdateEvent}
            onDelete={deleteEvent}
            onDuplicate={duplicateEvent}
            members={members}
            groups={groups}
            externalContacts={externalContacts}
            seasons={seasons}
            venues={venues}
            events={events}
            event={editingEvent}
            initialDate={selectedDate}
            initialTime={selectedTime}
          />
        )}

        {showSettingsModal && (
          <SettingsModal
            onClose={() => { setShowSettingsModal(false); loadExternalContacts(); loadVenues(); }}
            members={members}
            groups={groups}
            onSaveMembers={saveMembers}
            onSaveGroups={saveGroups}
            onReload={() => loadAllData(currentDateRef.current)}
            session={session}
            preferences={{ weekStartsMonday, defaultView: view }}
            onSavePreferences={savePreferences}
            adminPrefs={{ isAdmin, reminderPhone, anniversaryDate }}
            onSaveAdminPrefs={saveAdminPrefs}
            vacationBlocks={vacationBlocks}
            onReloadVacations={loadVacationBlocks}
            onRequestNotifications={requestNotificationPermission}
          />
        )}
      </div>
    </div>
  );
};

export default FamilyCalendar;
